import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-values-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule
  ],
  template: `
    <h2 mat-dialog-title class="text-lg font-semibold">Valores predefinidos</h2>

    <mat-dialog-content [formGroup]="form" class="flex flex-col gap-3 max-h-[60vh] overflow-auto p-4 bg-gray-50 rounded-md">
      <div formArrayName="values">
        <div *ngFor="let val of valuesArray.controls; let i = index" 
             [formGroupName]="i" 
             class="flex gap-2 items-center p-2 bg-white border border-gray-300 rounded-md shadow-sm">

          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>Código</mat-label>
            <input matInput formControlName="id" />
            <mat-error *ngIf="val.get('id')?.hasError('required')">Código es requerido</mat-error>
            <mat-error *ngIf="val.get('id')?.hasError('duplicate')">Código duplicado</mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>Valor</mat-label>
            <input matInput formControlName="name" />
            <mat-error *ngIf="val.get('name')?.hasError('required')">Valor es requerido</mat-error>
          </mat-form-field>

          <button mat-icon-button color="warn" type="button" (click)="removeValue(i)">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      </div>

      <button mat-stroked-button color="primary" type="button" (click)="addValue()" class="mt-3">
        <mat-icon>add</mat-icon> Agregar Valor
      </button>
    </mat-dialog-content>

    <mat-dialog-actions align="end" class="p-4 border-t border-gray-200">
      <button mat-button (click)="close()">Cancelar</button>
      <button mat-flat-button color="primary" (click)="save()" [disabled]="!canSave">Guardar</button>
    </mat-dialog-actions>
  `
})
export class ValuesModalComponent implements OnInit {
  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ValuesModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { values: any[] }
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      values: this.fb.array(this.data.values?.map(v => this.createValueGroup(v)) || [])
    });

    // Validar duplicados cada vez que cambia un valor
    this.valuesArray.valueChanges.subscribe(() => this.validateDuplicates());
  }

  get valuesArray(): FormArray {
    return this.form.get('values') as FormArray;
  }

  private createValueGroup(value?): FormGroup {
    return this.fb.group({
      id: [value?.id || '', [Validators.required]],
      name: [value?.name || '', [Validators.required]]
    });
  }

  addValue() {
    this.valuesArray.push(this.createValueGroup());
  }

  removeValue(index: number) {
    this.valuesArray.removeAt(index);
    this.validateDuplicates(); // Revalidar duplicados
  }

  private validateDuplicates() {
    const ids = this.valuesArray.controls.map(ctrl => ctrl.get('id')?.value?.trim() || '');
    this.valuesArray.controls.forEach(ctrl => {
      const id = ctrl.get('id')?.value?.trim() || '';
      if (id && ids.filter(c => c === ids).length > 1) {
        ctrl.get('id')?.setErrors({ duplicate: true });
      } else {
        const errors = ctrl.get('id')?.errors;
        if (errors) {
          delete errors['duplicate'];
          if (Object.keys(errors).length === 0) ctrl.get('id')?.setErrors(null);
        }
      }
    });
  }

  get canSave(): boolean {
    if (this.form.invalid) return false;
    return !this.valuesArray.controls.some(ctrl => ctrl.get('id')?.hasError('duplicate'));
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    if (!this.canSave) return;
    this.dialogRef.close(this.valuesArray.getRawValue());
  }
}
